import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PackageDataHomeV2 } from 'components/modules/patientArea/home/homeV2/types'
import nestApiService from 'services/api/nest-api-service'
import { bundleMounjaroComboPromotions, bundleMounjaroStarterPromotion } from 'utils/products/mounjaroPromotion'

export interface SelectedPackageSubscriptions {
  [productId: string]: number | null // key: productId (string for composite keys like "1114-2339"), value: variantId
}
export type MedleveConsultationType = 'weight_loss' | 'weight_loss_plus' | undefined
export type MedleveCommunicationMedium = 'chat' | 'visio' | undefined

export interface HomeSliceState {
  isLoading: boolean
  activeStep: number
  packageData: PackageDataHomeV2
  selectedPackages: SelectedPackageSubscriptions
  isShowingSingleDrugSelector: boolean
  isShowingWeightHome: boolean
  isAboutSubscriptionModalOpen: boolean
  isGLP1UpsellModalOpen: boolean
  isMounjaroLillyModalOpen: boolean
  activePathologyTab: string | null
  activeSpecialistTab: string | null
  medleveConsultationType: MedleveConsultationType
  medleveCommunicationMedium: MedleveCommunicationMedium
}

const initialState: HomeSliceState = {
  isLoading: false,
  activeStep: 1,
  packageData: {
    subscriptions: [],
    singles: []
  },
  selectedPackages: {},
  isShowingSingleDrugSelector: false,
  isAboutSubscriptionModalOpen: false,
  isGLP1UpsellModalOpen: false,
  isMounjaroLillyModalOpen: false,
  activePathologyTab: null,
  activeSpecialistTab: null,
  isShowingWeightHome: false,
  medleveConsultationType: undefined,
  medleveCommunicationMedium: undefined
}

export const homeSliceSlice = createSlice({
  name: 'home-slice',
  initialState,
  reducers: {
    resetHomeSliceState: (state) => {
      state = initialState
      return state
    },
    setHomeActiveStep: (state, action: PayloadAction<number>) => {
      state.activeStep = action.payload
    },
    setSelectedPackages: (state, action: PayloadAction<SelectedPackageSubscriptions>) => {
      state.selectedPackages = action.payload
    },
    updateSelectedPackages: (state, action: PayloadAction<SelectedPackageSubscriptions>) => {
      state.selectedPackages = { ...state.selectedPackages, ...action.payload }
    },
    setIsShowingSingleDrugSelector: (state, action: PayloadAction<boolean>) => {
      state.isShowingSingleDrugSelector = action.payload
    },
    setActivePathologyTab: (state, action: PayloadAction<string>) => {
      state.activePathologyTab = action.payload
    },
    setActiveSpecialistTab: (state, action: PayloadAction<string>) => {
      state.activeSpecialistTab = action.payload
    },
    onAboutSubscriptionModalOpen: (state) => {
      state.isAboutSubscriptionModalOpen = true
    },
    onAboutSubscriptionModalClose: (state) => {
      state.isAboutSubscriptionModalOpen = false
    },
    onGLP1UpsellModalOpen: (state) => {
      state.isGLP1UpsellModalOpen = true
    },
    onGLP1UpsellModalClose: (state) => {
      state.isGLP1UpsellModalOpen = false
    },
    // ECS-617 — Lilly manufacturer-program modal shown between address submit
    // and the checkout drawer when Mounjaro is in the cart.
    onMounjaroLillyModalOpen: (state) => {
      state.isMounjaroLillyModalOpen = true
    },
    onMounjaroLillyModalClose: (state) => {
      state.isMounjaroLillyModalOpen = false
    },
    setIsShowingWeightHome: (state, action: PayloadAction<boolean>) => {
      state.isShowingWeightHome = action.payload
    },
    setMedleveConsultationType: (state, action: PayloadAction<MedleveConsultationType>) => {
      state.medleveConsultationType = action.payload
    },
    setMedleveCommunicationMedium: (state, action: PayloadAction<MedleveCommunicationMedium>) => {
      state.medleveCommunicationMedium = action.payload
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getPackagesHomeV2.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getPackagesHomeV2.fulfilled, (state, action) => {
        state.isLoading = false
        state.packageData = action.payload
      })
      .addCase(getPackagesHomeV2.rejected, (state) => {
        state.isLoading = false
      })
})

export const {
  resetHomeSliceState,
  setHomeActiveStep,
  setSelectedPackages,
  updateSelectedPackages,
  setIsShowingSingleDrugSelector,
  setActivePathologyTab,
  setActiveSpecialistTab,
  onAboutSubscriptionModalOpen,
  onAboutSubscriptionModalClose,
  onGLP1UpsellModalOpen,
  onGLP1UpsellModalClose,
  onMounjaroLillyModalOpen,
  onMounjaroLillyModalClose,
  setIsShowingWeightHome,
  setMedleveConsultationType,
  setMedleveCommunicationMedium
} = homeSliceSlice.actions

export default homeSliceSlice.reducer

interface GetPackagesParams {
  patientId: number
}

export const getPackagesHomeV2 = createAsyncThunk(
  'get-packages-home-v2',
  async ({ patientId }: GetPackagesParams, { rejectWithValue }) => {
    try {
      const { data } = await nestApiService.apiInstance.get<PackageDataHomeV2>(`/packages/recommended/${patientId}`)
      return { ...data, singles: bundleMounjaroComboPromotions(bundleMounjaroStarterPromotion(data.singles)) }
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)
